// Global Instructions Rule Applied!

import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { CardConfig } from '../../interfaces.ts'
import { DealKanbanItem } from '../../../../../app_modules/deals/components/pipelines/kanbans/early_stage/interfaces.ts'
import DealAgeChips from '../../../../../app_modules/deals/components/pipelines/kanbans/early_stage/DealAgeChips.tsx'
import { DealFields } from '../../../../../state_manager/data_models/deal_record.ts'
import type { Currency } from '../../../../../app_modules/currencies/interfaces.ts'
import { PipelineConfigModel } from '../../../../../state_manager/data_models/pipeline_config_model.ts'
import { PipelinesModel } from '../../../../../state_manager/data_models/pipelines_model.ts'
import { daysSince, resolveBandColour } from '../../../../utils/pipelineAge.ts'
import { FieldTypeEnum, type NumberFieldConfig, type ValuationFieldConfig } from '../../../InputManager/types.ts'
import { InputManager } from '../../../InputManager/index.ts'
import TaskListMini from '../../../../../app_modules/tasks/components/TaskListMini.tsx'
import type { ViewTaskConfig } from '../../../../../ui_configs/fund_management/view_task_config.ts'

interface UseEarlyStageCardConfigParams {
  handleOpenDrawerForDeal: (dealId: string) => void
  handleTasksClick: (allTasks: unknown[], record: unknown, taskId: string | null) => void
  handleTaskToggle: (taskId: string, recordId: string, completed: boolean) => void
  getCurrencyById: (currencyId: string | null | undefined) => Currency | null
  handlePatchSuccess: (recordId: string, updates: Record<string, unknown>) => void
  handleCompanyNameClick: (e: React.MouseEvent, companyId: string, companyName: string) => void
  handleDealNameClick?: (e: React.MouseEvent, dealId: string, dealName: string) => void
  taskConfig?: ViewTaskConfig
}

export function useEarlyStageCardConfig({
  handleOpenDrawerForDeal,
  handleTasksClick,
  handleTaskToggle,
  getCurrencyById,
  handlePatchSuccess,
  handleCompanyNameClick,
  handleDealNameClick,
  taskConfig
}: UseEarlyStageCardConfigParams): CardConfig<DealKanbanItem> {
  // Threshold + colour config flows through the cached global configs the board
  // already subscribes to — no extra fetch. days-in-stage bands live on the
  // deal's pipeline_config row; days-in-pipeline bands on its pipeline.
  const pipelineConfigs = useSelector(
    (state: { globalConfigs?: { pipeline_configs?: PipelineConfigModel[] } }) =>
      state.globalConfigs?.pipeline_configs || []
  )
  const pipelines = useSelector(
    (state: { globalConfigs?: { pipelines?: PipelinesModel[] } }) => state.globalConfigs?.pipelines || []
  )

  return useMemo(
    () => ({
      getItemId: (item) => item[DealFields.deals__id] as string,
      getItemTitle: (item) => (item[DealFields.deals__name] as string | null | undefined) || 'Unnamed Deal',
      getItemTitleDisplay: handleDealNameClick
        ? (item) => {
            const dealId = item[DealFields.deals__id] as string
            const dealName = (item[DealFields.deals__name] as string | null | undefined) || 'Unnamed Deal'
            return (
              <span
                className='text-blue-600 hover:text-blue-800 hover:underline cursor-pointer'
                onClick={(e) => handleDealNameClick(e, dealId, dealName)}
              >
                {dealName}
              </span>
            )
          }
        : undefined,
      getItemSubtitle: (item) => {
        const companyName = (item[DealFields.accounts__name] as string | null | undefined) || null
        if (!companyName) return null

        // Get company ID from deals__account or accounts__id
        const companyId =
          (item[DealFields.deals__account] as string | null | undefined) ||
          (item[DealFields.accounts__id] as string | null | undefined) ||
          null

        // If we have a company ID, render as hyperlink
        if (companyId) {
          return (
            <span
              className='text-xs text-blue-600 hover:text-blue-800 hover:underline cursor-pointer'
              onClick={(e) => handleCompanyNameClick(e, companyId, companyName)}
            >
              {companyName}
            </span>
          )
        }

        // Otherwise, return plain text
        return companyName
      },
      onItemEdit: (item) => handleOpenDrawerForDeal(item[DealFields.deals__id] as string),
      renderItemMeta: (item) => {
        const deal = item as DealKanbanItem
        const daysInStage = daysSince(deal[DealFields.pipeline_deals__stage_date] as string | null | undefined)
        const daysInPipeline = daysSince(deal[DealFields.pipeline_deals__date_created] as string | null | undefined)

        const configId = deal[DealFields.pipeline_config__id] as string | null | undefined
        const cfg = pipelineConfigs.find((c) => c.id === configId)
        const pipeline = cfg ? pipelines.find((p) => p.id === cfg.pipeline) : undefined

        // Per-stage opt-out: hide both age counters when the stage disables them.
        if (cfg?.show_age_counters === false) return null

        const stageColour = resolveBandColour(
          daysInStage,
          cfg?.stage_warn_days,
          cfg?.stage_warn_colour,
          cfg?.stage_crit_days,
          cfg?.stage_crit_colour
        )
        const pipelineColour = resolveBandColour(
          daysInPipeline,
          pipeline?.pipeline_warn_days,
          pipeline?.pipeline_warn_colour,
          pipeline?.pipeline_crit_days,
          pipeline?.pipeline_crit_colour
        )

        return (
          <DealAgeChips
            daysInStage={daysInStage}
            stageColour={stageColour}
            daysInPipeline={daysInPipeline}
            pipelineColour={pipelineColour}
          />
        )
      },
      renderItemContent: (item, column) => {
        const deal = item as DealKanbanItem
        const tasks = (deal[DealFields.prog__tasks] as unknown[]) || []

        // Get valuation display
        const getDisplayValuation = () => {
          if (deal[DealFields.rounds__post_money_valuation] && deal[DealFields.rounds__post_money_valuation] !== 0) {
            return { value: deal[DealFields.rounds__post_money_valuation], type: 'Post' as const }
          } else if (
            deal[DealFields.rounds__pre_money_valuation] &&
            deal[DealFields.rounds__pre_money_valuation] !== 0
          ) {
            return { value: deal[DealFields.rounds__pre_money_valuation], type: 'Pre' as const }
          }
          if (deal[DealFields.prog__valuation] && deal[DealFields.prog__valuation] !== 0) {
            return {
              value: deal[DealFields.prog__valuation],
              type: (deal[DealFields.prog__valuation_type] || 'Post') as 'Pre' | 'Post'
            }
          }
          return null
        }

        const displayValuation = getDisplayValuation()

        // Field configurations
        const commitmentAmountFieldConfig: NumberFieldConfig = {
          key: DealFields.prog__commitment_amount,
          label: 'Commitment Amount',
          type: FieldTypeEnum.CURRENCY,
          value: deal[DealFields.prog__commitment_amount] as number | null | undefined,
          currencySymbol: getCurrencyById(deal[DealFields.deals__currency] as string | null | undefined)?.symbol || '$',
          editable: false
        }

        const valuationFieldConfig: ValuationFieldConfig = {
          key: DealFields.rounds__pre_money_valuation,
          label: `${displayValuation?.type || 'Post'} Money Valuation`,
          type: FieldTypeEnum.VALUATION,
          value: {
            preMoneyValue: deal[DealFields.rounds__pre_money_valuation] as number | null | undefined,
            postMoneyValue: deal[DealFields.rounds__post_money_valuation] as number | null | undefined
          },
          currencySymbol: getCurrencyById(deal[DealFields.deals__currency] as string | null | undefined)?.symbol || '$',
          showValuationType: false,
          editable: true,
          patchFields: [
            { sourceKey: 'preMoneyValue', targetField: 'pre_money_valuation' },
            { sourceKey: 'postMoneyValue', targetField: 'post_money_valuation' }
          ],
          patchEndpoint: 'rounds',
          valueTransformer: (value: { preMoneyValue?: number; postMoneyValue?: number }) => ({
            preMoneyValue: value?.preMoneyValue || null,
            postMoneyValue: value?.postMoneyValue || null,
            [DealFields.rounds__pre_money_valuation]: value?.preMoneyValue || null,
            [DealFields.rounds__post_money_valuation]: value?.postMoneyValue || null
          })
        }

        const raiseAmountFieldConfig: NumberFieldConfig = {
          key: DealFields.rounds__raise_amount,
          label: 'Raise Amount',
          type: FieldTypeEnum.CURRENCY,
          value: deal[DealFields.rounds__raise_amount] as number | null | undefined,
          currencySymbol: getCurrencyById(deal[DealFields.deals__currency] as string | null | undefined)?.symbol || '$',
          editable: true
        }

        const allocationFieldConfig: NumberFieldConfig = {
          key: DealFields.deals__allocation_available,
          label: 'Allocation Available',
          type: FieldTypeEnum.CURRENCY,
          value: deal[DealFields.deals__allocation_available] as number | null | undefined,
          currencySymbol: getCurrencyById(deal[DealFields.deals__currency] as string | null | undefined)?.symbol || '$',
          editable: true
        }

        const hasFinancialInfo =
          deal[DealFields.prog__commitment_amount] ||
          displayValuation ||
          deal[DealFields.rounds__raise_amount] ||
          deal[DealFields.deals__allocation_available]

        const showTasks = taskConfig?.showTasks !== false
        const taskListProps = showTasks && taskConfig
          ? {
              maxVisible: taskConfig.maxVisible ?? 3,
              showCompleted: taskConfig.showCompleted ?? false
            }
          : { maxVisible: 3, showCompleted: false }

        return (
          <>
            {/* Tasks Section */}
            {showTasks && tasks.length > 0 && (
              <div className='mt-1 mb-2'>
                {React.createElement(TaskListMini as React.ComponentType<any>, {
                  tasks: tasks,
                  maxVisible: taskListProps.maxVisible,
                  maxTextLength: 40,
                  showCompleted: taskListProps.showCompleted,
                  onTaskClick: (task: unknown, allTasks: unknown[], record: unknown) => {
                    handleTasksClick(allTasks, record, (task as { tasks__id: string }).tasks__id)
                  },
                  onTaskToggle: (taskId: string, recordId: string, completed: boolean) => {
                    handleTaskToggle(taskId, recordId, completed)
                  },
                  record: deal
                })}
              </div>
            )}

            {/* Financial Information */}
            {hasFinancialInfo && (
              <div className={`space-y-1 pt-1 ${!deal[DealFields.prog__commitment_amount] ? 'border-t' : ''}`}>
                {/* Commitment Amount */}
                {deal[DealFields.prog__commitment_amount] && (
                  <div className='border-b'>
                    <InputManager
                      key={`commitment-amount-${deal[DealFields.deals__id] as string}`}
                      inputMode='read'
                      labelPosition='inline'
                      alignment='right'
                      fields={[commitmentAmountFieldConfig]}
                      showLabelColons={false}
                      fieldWrapping={true}
                      textClassName='text-xs font-medium'
                    />
                  </div>
                )}

                {/* Valuation Fields */}
                {displayValuation &&
                  (deal[DealFields.rounds__pre_money_valuation] || deal[DealFields.rounds__post_money_valuation]) &&
                  deal[DealFields.rounds__id] && (
                    <div className='space-y-1'>
                      <InputManager
                        key={`pre-valuation-${deal[DealFields.deals__id] as string}`}
                        inputMode='read'
                        labelPosition='inline'
                        alignment='right'
                        fields={[valuationFieldConfig]}
                        onPatchSuccess={handlePatchSuccess}
                        enableAutoPatch={true}
                        patchId={deal[DealFields.rounds__id] as string}
                        recordId={deal[DealFields.deals__id] as string}
                        showLabelColons={false}
                        fieldWrapping={true}
                        textClassName='text-xs font-medium'
                      />
                    </div>
                  )}

                {/* Raise Amount */}
                {deal[DealFields.rounds__raise_amount] && (
                  <InputManager
                    key={`raise-amount-${deal[DealFields.deals__id] as string}`}
                    inputMode='read'
                    labelPosition='inline'
                    alignment='right'
                    fields={[raiseAmountFieldConfig]}
                    onPatchSuccess={handlePatchSuccess}
                    enableAutoPatch={true}
                    patchId={deal[DealFields.rounds__id] as string}
                    recordId={deal[DealFields.deals__id] as string}
                    showLabelColons={false}
                    fieldWrapping={true}
                    textClassName='text-xs font-medium'
                  />
                )}

                {/* Allocation Available */}
                {deal[DealFields.deals__allocation_available] && (
                  <InputManager
                    key={`allocation-${deal[DealFields.deals__id] as string}`}
                    inputMode='read'
                    labelPosition='inline'
                    alignment='right'
                    fields={[allocationFieldConfig]}
                    onPatchSuccess={handlePatchSuccess}
                    enableAutoPatch={true}
                    patchId={deal[DealFields.deals__id] as string}
                    recordId={deal[DealFields.deals__id] as string}
                    showLabelColons={false}
                    fieldWrapping={true}
                    textClassName='text-xs font-medium'
                  />
                )}
              </div>
            )}
          </>
        )
      }
    }),
    [
      handleOpenDrawerForDeal,
      handleTasksClick,
      handleTaskToggle,
      getCurrencyById,
      handlePatchSuccess,
      handleCompanyNameClick,
      handleDealNameClick,
      taskConfig,
      pipelineConfigs,
      pipelines
    ]
  )
}
